import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Services', href: '#services' },
    { label: 'Process', href: '#process' },
    { label: 'Social', href: '#social' },
    { label: 'About', href: '#about' },
    { label: 'Blog', href: '#blog' },
    { label: 'FAQ', href: '#faq' },
    { label: 'Contact', href: '#contact' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        isScrolled ? 'glass-header py-4' : 'bg-transparent py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <nav className="flex items-center justify-between">
          {/* Logo — matches footer wordmark */}
          <motion.a
            href="/"
            data-testid="logo-link"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center leading-none"
          >
            <span
              className="text-2xl sm:text-3xl font-black tracking-[-0.04em] text-[#1C1917]"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Hype
              <span className="italic font-light text-[#AA94FF]">Lab</span>
              <span className="text-[#FFA952]">.</span>
            </span>
          </motion.a>

          {/* Desktop Navigation */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="hidden lg:flex items-center gap-8"
          >
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => scrollToSection(item.href)}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className="text-sm font-medium text-[#595959] hover:text-[#1C1917] transition-colors duration-300 tracking-wide"
              >
                {item.label}
              </button>
            ))}
          </motion.div>

          {/* CTA Button */}
          <motion.button
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            onClick={() => scrollToSection('#contact')}
            data-testid="header-cta-button"
            className="hidden lg:block bg-[#AA94FF] text-[#F4F4F3] rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wider hover:scale-105 hover:shadow-lg hover:bg-[#CC5003] transition-all duration-300"
          >
            Get Started
          </motion.button>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-[#1C1917]"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#FDFBF7] border-t border-[#E2DFD8]"
          >
            <div className="px-6 py-6 space-y-4">
              {navItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => scrollToSection(item.href)}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  className="block w-full text-left text-lg font-medium text-[#595959] hover:text-[#1C1917] transition-colors py-2"
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => scrollToSection('#contact')}
                data-testid="mobile-cta-button"
                className="w-full bg-[#AA94FF] text-[#F4F4F3] rounded-full px-6 py-4 text-sm font-bold uppercase tracking-wider mt-4 hover:bg-[#CC5003]"
              >
                Get Started
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
